/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { notificationService } from '../services/notifications'
import { useAuth } from '../hooks/useAuth'

interface NotificationContextValue {
  unreadCount: number
  setUnreadCount: (count: number) => void
  decrementUnread: (by?: number) => void
  clearUnread: () => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0)
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (!isAuthenticated || !user?.feature_flags?.new_homepage) return
    notificationService.getUnreadCount().then(setUnreadCount).catch(() => {})
  }, [isAuthenticated, user?.feature_flags?.new_homepage])

  const decrementUnread = useCallback((by = 1) => {
    setUnreadCount(prev => Math.max(0, prev - by))
  }, [])

  const clearUnread = useCallback(() => {
    setUnreadCount(0)
  }, [])

  return (
    <NotificationContext.Provider value={{ unreadCount, setUnreadCount, decrementUnread, clearUnread }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotificationContext() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotificationContext must be used within NotificationProvider')
  return ctx
}
